import useEmblaCarousel from 'embla-carousel-react';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { shuffle } from 'remeda';

import { useIsSSR } from '@/hooks/use-is-ssr.ts';
import { type TestimonialsData } from '@/lib/api/requests.ts';
import { CircularLoader } from '@/ui/CircularLoader.tsx';

import googleLogo from './internal/google-logo.svg';
import quote from './internal/quote.svg';
import star from './internal/star.svg';
import trustpilotLogo from './internal/trustpilot-logo.svg';

export const TestimonialsRating = (props: { data: TestimonialsData }) => {
  const { google, trustpilot } = props.data;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center gap-3 sm:items-start">
        <div className="flex items-center gap-2">
          <img src={googleLogo} alt="" className="-mt-0.5 size-6" />
          <h3 className="typography-v1-h4">Google Reviews</h3>
        </div>
        <p className="typography-v1-body4">
          Vurdert {google.rating}/5 stjerner - {google.count} anmeldelser
        </p>
      </div>

      <div className="flex flex-col items-center gap-3 sm:items-start">
        <div className="flex items-center gap-2">
          <img src={trustpilotLogo} alt="" className="-mt-1 size-7" />
          <h3 className="typography-v1-h4">Trustpilot</h3>
        </div>
        <p className="typography-v1-body4">
          Vurdert {trustpilot.rating}/5 stjerner - {trustpilot.count} anmeldelser
        </p>
      </div>
    </div>
  );
};

export const Testimonials = (props: { data: TestimonialsData }) => {
  const { reviews } = props.data;

  return (
    <section className="isolate flex flex-col gap-10 sm:flex-row-reverse sm:gap-8 lg:gap-12">
      <div className="flex flex-1 flex-col items-center gap-4 sm:items-start sm:text-left">
        <h2 className="mb-1 max-w-xs text-center typography-v1-h2 text-pretty sm:text-left sm:typography-v1-h1">
          Se hvorfor andre elsker oss
        </h2>

        <TestimonialsRating data={props.data} />
      </div>

      <Carousel reviews={reviews} />
    </section>
  );
};

const Carousel = (props: { reviews: { name: string; text: string; date: string }[] }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: 'start' });
  const [prevButtonDisabled, setPrevButtonDisabled] = useState(false);
  const [nextButtonDisabled, setNextButtonDisabled] = useState(false);
  // Testimonials are randomized, to keep that behavior with SSG we need to show a loading state
  // Otherwise it will cause a hydration issue and flashing content
  // This could be avoided by using proper SSR or not randomizing the testimonials
  const isSSR = useIsSSR();
  const shuffledReviews = useMemo(() => shuffle(props.reviews), [props.reviews]);

  useEffect(() => {
    if (!emblaApi) return;

    const toggleButtonsDisabled = () => {
      setPrevButtonDisabled(!emblaApi?.canGoToPrev());
      setNextButtonDisabled(!emblaApi?.canGoToNext());
    };

    toggleButtonsDisabled();

    emblaApi.on('reinit', toggleButtonsDisabled);
    emblaApi.on('select', toggleButtonsDisabled);
  }, [emblaApi]);

  return (
    <div className="relative mx-auto flex h-82 w-72 flex-col rounded-2xl bg-v1-background-200 pt-10 pb-2 lg:w-140 xl:h-90">
      {isSSR ? (
        <div className="grid size-full place-items-center pb-8">
          <CircularLoader />
        </div>
      ) : (
        <div className="flex flex-1 flex-col overflow-hidden [--slide-size:85%] [--slide-spacing:1rem] lg:[--slide-size:45%]">
          <div className="flex flex-1 flex-col overflow-hidden px-6" ref={emblaRef}>
            <div className="-ml-(--slide-spacing) flex max-h-full flex-1 touch-pan-y touch-pinch-zoom">
              {shuffledReviews.map((review, index) => (
                <div
                  key={index}
                  className="h-full min-w-0 flex-0 shrink-0 basis-(--slide-size) pl-(--slide-spacing)"
                >
                  <div className="flex h-full flex-col rounded-2xl bg-white p-4 typography-v1-body4">
                    <div className="mb-2 flex items-center gap-1">
                      <img src={star} alt="" className="size-4" />
                      <img src={star} alt="" className="size-4" />
                      <img src={star} alt="" className="size-4" />
                      <img src={star} alt="" className="size-4" />
                      <img src={star} alt="" className="size-4" />
                    </div>

                    <p className="mb-1 min-w-0 flex-1 overflow-y-auto text-pretty hyphens-auto whitespace-pre-line">
                      {review.text}
                    </p>

                    <b className="mt-auto typography-v1-caption1">{review.name},</b>
                    <p className="typography-v1-caption1">{review.date}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-2 flex shrink-0 justify-end">
            <button
              onClick={() => emblaApi?.goToPrev()}
              disabled={prevButtonDisabled}
              className="size-8 rounded-full text-white disabled:opacity-50"
            >
              <ArrowLeftIcon />
            </button>
            <button
              onClick={() => emblaApi?.goToNext()}
              disabled={nextButtonDisabled}
              className="size-8 rounded-full text-white disabled:opacity-50"
            >
              <ArrowRightIcon />
            </button>
          </div>
        </div>
      )}
      <img src={quote} alt="" className="absolute top-0 left-0 size-15 -translate-1/2" />
    </div>
  );
};
